import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';
import { useData } from '../../../contexts/data-context/use-data';
import { CardView } from '../../../types/entity-types';
import { patchSwrFetcher } from '../../../utils/patch-swr-fetcher';

const CARDS_DISPLAY_NAME_API_PATH = '/api/cards/display-name';

type UpdateCardDisplayNameRequest = {
  cardId: string;
  displayName: string;
};

export const useUpdateCardDisplayName = () => {
  const { data, mutations } = useData();

  const { trigger, isMutating } = useSWRMutation<CardView, Error, string, UpdateCardDisplayNameRequest>(
    CARDS_DISPLAY_NAME_API_PATH,
    patchSwrFetcher,
  );

  const updateDisplayName = async (cardId: string, displayName: string): Promise<CardView | undefined> => {
    try {
      const updatedCard = await trigger({ cardId, displayName });

      if (updatedCard) {
        toast.success('Card nickname updated');

        if (data?.cards) {
          await mutations.mutateCache?.({
            cards: data.cards.map((card) => (card.id === cardId ? { ...card, displayName } : card)),
          });
        }
      }

      return updatedCard;
    } catch (error) {
      Sentry.captureException(error, {
        extra: {
          cardId,
          displayName,
          context: 'updateCardDisplayName',
        },
      });
      toast.error('Failed to update card nickname. Please try again.');
      throw error;
    }
  };

  return {
    updateDisplayName,
    isUpdating: isMutating,
  };
};
